import { useEffect } from 'react'
import {
  useParams,
  Outlet,
  useSearchParams,
  useLocation,
  useNavigate,
} from 'react-router'
import { Alert } from '@mui/material'
import useLocalStorage from '@/hooks/useLocalStorage'
import { useGetProject } from '@/service-library/hooks/projects'
import Navigation from '@/components/navigation/Navigation'
import OrganizationsProvider from '@/components/organizations/OrganizationsProvider'
import PageTitle from '@/components/PageTitle'
import FileUploadProvider from '@/components/upload-files/FileUploadProvider'
import SelectedWorkflowProvider from '@/components/workflows/SelectedWorkflowProvider'
import SelectedWorkflowStateProvider from '@/components/workflows/SelectedWorkflowStateProvider'
import ViewsProvider from '@/components/workflows/ViewsProvider'
import WorkflowsProvider from '@/components/workflows-provider/WorkflowsProvider'
import ProjectBreadcrumb from './ProjectBreadcrumb'
import ProjectProvider from './ProjectProvider'

const pathsThatMightIncludeTab = ['settings/workflow-states', 'sources']
const pathsThatMightIncludeRuleId = ['field-rules', 'document-rules']

export default function ProjectLayout() {
  const { projectId, view } = useParams()
  const [searchParams, setSearchParams] = useSearchParams()
  const { pathname } = useLocation()
  const navigate = useNavigate()

  const [projectPreferredTab] = useLocalStorage(
    'project-preferred-tab',
    'documents',
  )
  // Legacy localStorage value: 'dashboard' was renamed to 'metrics'
  const normalizedPreferredTab =
    projectPreferredTab === 'dashboard' ? 'metrics' : projectPreferredTab

  const { project, error, queryKey } = useGetProject({
    id: projectId as string,
    filters: {
      fields__include: 'project_category,project_grids,tags',
      project_grids__fields__include:
        'project_grid_fields,sub_project_grid_fields',
      project_grids__project_grid_fields__fields__include:
        'full_name,project_grid_field_type,project_grid_id,included_grid_fields,included_grid_fields_ids,project_grid_field_rules,name,has_category_model,project_model_id,live_list_filter_config',
      project_grids__project_grid_fields__project_grid_field_rules__fields__include:
        'rule',
    },
  })

  useEffect(() => {
    // If we're not on a sub view, redirect to preferred tab
    if (project && new RegExp(`/projects/${project.id}/?$`).test(pathname)) {
      navigate(normalizedPreferredTab, {
        replace: true,
      })
    }
  }, [navigate, pathname, project, normalizedPreferredTab])

  // We want to keep the query params to keep the workflow id, but we don't
  // want to keep the tab query param when not in the sources tab
  useEffect(() => {
    const pathAllowsTab = pathsThatMightIncludeTab.some((path) =>
      pathname.includes(path),
    )
    const pathAllowsRuleId = pathsThatMightIncludeRuleId.some((path) =>
      pathname.includes(path),
    )

    const isOnDashboard =
      pathname.includes(`${projectId}/metrics`) ||
      pathname.includes(`${projectId}/model`)

    if (
      (searchParams.has('tab') && !pathAllowsTab) ||
      (searchParams.has('project_model') && !isOnDashboard) ||
      (searchParams.has('rule_id') && !pathAllowsRuleId)
    ) {
      setSearchParams((prev) => {
        if (!pathAllowsTab) {
          prev.delete('tab')
        }
        if (!isOnDashboard) {
          prev.delete('project_model')
        }
        if (!pathAllowsRuleId) {
          prev.delete('rule_id')
        }
        return prev
      })
    }
  }, [pathname, projectId, searchParams, setSearchParams])

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: 0,
      }}
    >
      {project && (
        <>
          <ProjectBreadcrumb
            label={project.name}
            url={`/projects/${project.id}/${view || 'documents'}`}
          />
          <PageTitle>{project.name}</PageTitle>
        </>
      )}
      {!!error && <Alert severity="error">{(error as Error).message}</Alert>}
      {project && (
        <ProjectProvider project={project} queryKey={queryKey}>
          <OrganizationsProvider orgId={project.org_id}>
            {/* Workflows and Workflow States */}
            <WorkflowsProvider>
              <SelectedWorkflowProvider>
                <SelectedWorkflowStateProvider>
                  {/* Other Providers */}
                  <ViewsProvider>
                    <FileUploadProvider project={project}>
                      <Navigation />
                      <Outlet />
                    </FileUploadProvider>
                  </ViewsProvider>
                </SelectedWorkflowStateProvider>
              </SelectedWorkflowProvider>
            </WorkflowsProvider>
          </OrganizationsProvider>
        </ProjectProvider>
      )}
    </div>
  )
}
